import React, { ReactElement, useEffect, useState } from 'react'
import {
  AsyncButton,
  Button,
  ButtonStyle,
  alertSuccess,
  IGiftCardResponse,
  Modal,
  InputField,
  NumberField,
  formatMoney,
  InputType,
  colors,
  hasRole,
  Role,
  GiftCardStatus,
  GiftItemType,
} from '@one-tree/library'
import styled from 'styled-components'

import { validate } from 'email-validator'
import { useHistory } from 'react-router-dom'
import { partialRedeemGiftCard, redeemGiftCard } from '../../helpers/APIHelper'
import { StateAction } from '../../types/Aliases'
import { useOrganisation } from '../../context/OrganisationProvider'
import { isGiftCardValid } from '../../helpers/GiftCardHelper'
import { RoutePath } from '../../types/Routes'

const Header = styled.h3`
  margin: 0 0 5px 0;
`
const Inputs = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  width: 300px;
`
const NumberRow = styled.div`
  display: flex;
  align-items: center;
  column-gap: 10px;
`
const StyledNumberField = styled(NumberField)`
  max-width: 90px;
`
const OutOf = styled.h3`
  font-size: 1.2rem;
  font-weight: bold;
`
const Optional = styled.div<{ isPartial: boolean }>`
  max-height: ${(props): string => (props.isPartial ? '170px' : '0')};
  overflow: hidden;
  transition: max-height 0.2s ease-in-out;
  p {
    margin: 0 0 10px;
  }
  input {
    margin-top: 2px;
    margin-bottom: 9px;
  }
`
const Buttons = styled.div`
  display: flex;
  justify-content: space-between;
`
const Error = styled.div`
  color: ${colors.red};
  font-weight: bold;
  height: 16px;
  text-align: right;
`
interface IGiftCardActions {
  giftCard: IGiftCardResponse
  setGiftCard: StateAction<IGiftCardResponse | false>
  setJustRedeemed: StateAction<boolean>
}
export default function GiftCardRedeem(props: IGiftCardActions): ReactElement {
  const { organisation, organisationUser } = useOrganisation()
  const { giftCard, setGiftCard, setJustRedeemed } = props

  const history = useHistory()
  const fromSales = history.location.state?.from === 'sales'

  const isMonetary = giftCard.type === GiftItemType.Monetary
  const hasPartial = Boolean(organisation?.hasPartialRedemption && isMonetary)

  const isValid = isGiftCardValid(giftCard.status, giftCard.startDate)

  const [modalOpen, setModalOpen] = useState(false)

  const [valueToRedeem, setValueToRedeem] = useState<number | null>(
    giftCard.value,
  )
  const [email, setEmail] = useState('')

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const isUser = organisationUser && hasRole(organisationUser.role, Role.OrganisationUser)
  const isAdmin = organisationUser && hasRole(organisationUser.role, Role.OrganisationAdmin)
  const hasStartDateInFuture = giftCard.startDate && giftCard.startDate > Date.now() / 1000
  const canForceRedeem = (
    giftCard.status !== GiftCardStatus.Redeemed
    && giftCard.status !== GiftCardStatus.Cancelled
    && (
      (giftCard.status === GiftCardStatus.Expired && (isUser || isAdmin))
      || (hasStartDateInFuture && isAdmin)
    )
  )

  // Entering an email is optional for admins
  const hasInvalidEmail = isUser
    ? Boolean(email && !validate(email))
    : !validate(email)

  const modalCancel = (): void => {
    setModalOpen(false)
    setValueToRedeem(giftCard.value)
    setEmail('')
    setError(false)
  }

  useEffect(() => {
    if (valueToRedeem === giftCard.value) setError(false)
  }, [valueToRedeem])

  const redeem = async (): Promise<void> => {
    const isFullRedeem = valueToRedeem === giftCard.value

    if (!isFullRedeem && hasInvalidEmail) {
      setError(true)
      return
    }

    setLoading(true)

    const result = isFullRedeem
      ? await redeemGiftCard({ cardId: giftCard.id })
      : await partialRedeemGiftCard({
          cardId: giftCard.id,
          value: valueToRedeem || 0,
          emailAddress: email,
        })

    if (result && result.id) {
      setGiftCard(result)
      setJustRedeemed(true)
      alertSuccess(
        isFullRedeem ? 'Redeem succeeded' : 'Partial redeem succeeded',
      )
      modalCancel()
    }

    setLoading(false)
  }

  const isPartial = (valueToRedeem || 0) < giftCard.value
  const fullAmount = formatMoney(giftCard.value, organisation?.currencyCode)
  const redeeming = formatMoney(valueToRedeem || 0, organisation?.currencyCode)
  const remainder = formatMoney(
    giftCard.value - (valueToRedeem || 0),
    organisation?.currencyCode,
  )

  return (
    <>
      <Modal
        modalOpen={modalOpen}
        onModalClose={(): void => setModalOpen(false)}
      >
        <Header>Redeem monetary voucher</Header>
        <Inputs>
          <NumberRow>
            <StyledNumberField
              onChange={setValueToRedeem}
              value={valueToRedeem}
              minimumValue={0}
              maximumValue={giftCard.value}
              isError={!valueToRedeem}
              disabled={loading}
            />
            <OutOf>/ {fullAmount}</OutOf>
          </NumberRow>
          <Optional isPartial={isPartial}>
            <p>
              You are redeeming {redeeming} out of {fullAmount}.
            </p>
            <p>
              A new voucher with a new code will be generated for the
              <strong> remaining {remainder}</strong>.
            </p>
            <InputField
              type={InputType.Email}
              placeholder="Email address"
              onChange={(value): void => {
                if (error) setError(false)
                setEmail(value)
              }}
              value={email}
              isLong={true}
              isError={error}
              disabled={loading}
            />
            {isUser && (
              <p>
                If you do not add an email address the voucher will be stored in
                the One Tree system for manual printing.
              </p>
            )}
          </Optional>
        </Inputs>
        <Error>{error && 'Invalid email'}</Error>
        <Buttons>
          <Button onClick={modalCancel}>Cancel</Button>
          <AsyncButton
            buttonStyle={ButtonStyle.Action}
            onClick={redeem}
            disabled={!valueToRedeem || error}
          >
            {isPartial ? 'Partial redeem' : 'Full redeem'}
          </AsyncButton>
        </Buttons>
      </Modal>

      {!isValid && (
        <Button
          buttonStyle={ButtonStyle.Secondary}
          onClick={(): void => history.push(fromSales ? RoutePath.Sales : RoutePath.Home)}
        >
          {fromSales ? 'Back' : 'Home'}
        </Button>
      )}

      {isValid && (
        <AsyncButton
          buttonStyle={ButtonStyle.Action}
          onClick={(): void | Promise<void> => (hasPartial ? setModalOpen(true) : redeem())}
        >
          Redeem
        </AsyncButton>
      )}

      {!isValid && canForceRedeem && (
        <AsyncButton
          buttonStyle={ButtonStyle.Danger}
          onClick={(): void | Promise<void> => (hasPartial ? setModalOpen(true) : redeem())}
          showConfirmation={true}
        >
          Redeem anyway
        </AsyncButton>
      )}
    </>
  )
}
